import { ErrorScreen } from "@dash/skeleton";
import { useTheme } from "@emotion/react";
import { ChartData, ChartOptions } from "chart.js";
import React from "react";
import { Bar } from "react-chartjs-2";
import tinycolor from "tinycolor2";

import { CubeCollapsedTileSkeletonStack } from "./CubeCollapsedTileSkeletonStack";
import { CubeExpandableChartFrame } from "./CubeExpandableChartFrame";
import type { CustomerAvailabilityByRegionRow } from "./parseCustomerAvailabilityByRegionRows";

type Props = {
  rows: CustomerAvailabilityByRegionRow[] | undefined;
  error: Error | null;
  isLoading: boolean;
};

export const CustomerAvailabilityByRegionChart: React.FC<Props> = ({ rows, error, isLoading }) => {
  const theme = useTheme();
  if (error) {
    return <ErrorScreen>{error.message}</ErrorScreen>;
  }
  if (isLoading) {
    return <CubeCollapsedTileSkeletonStack />;
  }
  if (rows === undefined || rows.length === 0) {
    return <div css={{ color: theme.colors.secondaryText, fontSize: "0.875rem" }}>Brak danych.</div>;
  }
  const labels = rows.map((r) => r.region);
  const data: ChartData<"bar"> = {
    labels,
    datasets: [
      {
        label: "Klienci",
        data: rows.map((r) => r.total),
        backgroundColor: tinycolor(theme.colors.primary).setAlpha(0.35).toRgbString(),
      },
      {
        label: "Z dostępnością",
        data: rows.map((r) => r.withAvailability),
        backgroundColor: theme.colors.primary,
      },
    ],
  };
  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: { beginAtZero: true },
    },
    plugins: {
      legend: { position: "top" },
    },
  };
  return (
    <CubeExpandableChartFrame modalTitle="Klienci z uzupełnioną dostępnością wg regionu" embedHeightPx={320}>
      {({ containerCss }) => (
        <div css={containerCss}>
          <Bar data={data} options={options} />
        </div>
      )}
    </CubeExpandableChartFrame>
  );
};
